import { useQuery } from 'react-query'
import {
    fetchMentorAIUsageData,
    fetchMentorAIUsageCsv,
    fetchNetworkMentorAIUsageData,
    fetchNetworkMentorAIUsageCSV,
} from '../apis/api-reports'
import { useReports } from '../../Context/ReportsContext'
import { useCohorts } from '../../Hooks/useCohorts'
import DayJS from '../../../../helpers/DayJS'
import { useMemo } from 'react'
import { useReportCSVDownload } from './useReportCSVDownload'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../../styles/globals/_variables'
import { defaultColumnChart } from '../Components/config/default.column.chart'
import { ReportProps } from '../Components/Report'
import { useTranslateChartHeaders } from './useTranslatedChartData'

export const useMentorAIUsage = () => {
    const { t } = useTranslation('reports-ai')
    const { reportQuery, enabled, staleTime, buildReportsQueryKey, calculateChartYAxisMax, isNetworkContext, networkId } =
        useReports()
    const { cohorts, from, to } = reportQuery
    const { networkCohorts } = useCohorts()
    const { eggColor } = colors

    const section = 'AI'
    const report = 'Mentor AI Usage'
    const title = t(report)

    // Get courseIds from network cohorts if in network context
    const selectedCohortIds = isNetworkContext
        ? networkCohorts
              ?.filter((nc) => cohorts?.length === 0 || cohorts?.includes(nc.startDate.substring(0, 10)))
              .map((c) => c.courseIdMongo) || []
        : []

    // Choose API based on context
    const fetchFunction = isNetworkContext
        ? () => fetchNetworkMentorAIUsageData({ networkId: networkId as string, cohorts: selectedCohortIds, from, to })
        : () => fetchMentorAIUsageData(reportQuery)

    const queryKey = isNetworkContext
        ? [networkId, 'Network Reports', section, report, `Cohorts: ${selectedCohortIds.join()}`, from, to]
        : buildReportsQueryKey({ section, report })

    const { data, ...rest } = useQuery(queryKey, fetchFunction, {
        enabled: isNetworkContext ? !!networkId && selectedCohortIds.length > 0 : enabled,
        staleTime: isNetworkContext ? 0 : staleTime,
    })

    const vAxisTitle = `${t('total questions')}: ${data?.totalQuestions}`
    const viewWindow = useMemo(() => calculateChartYAxisMax({ rows: data?.barChart }), [data, calculateChartYAxisMax])

    const chart: ReportProps['chart'] = {
        ...defaultColumnChart,
        data: useTranslateChartHeaders({ chartData: data?.barChart, i18nNamespace: 'reports-ai' }),
        options: {
            ...defaultColumnChart.options,
            colors: [eggColor],
            vAxis: {
                title: vAxisTitle,
                viewWindow,
            },
        },
    }

    // CSV download
    const fileName = `mentor-ai-usage_${DayJS().format('YYYY-MM-DD_HH-mm')}.csv`
    const { onExportCSV, exporting } = useReportCSVDownload({
        downloadFn: isNetworkContext
            ? () => fetchNetworkMentorAIUsageCSV({ networkId: networkId as string, cohorts: selectedCohortIds, from, to })
            : () => fetchMentorAIUsageCsv(reportQuery),
        fileName,
    })

    const chartProps = {
        title,
        chart,
        onExportCSV,
        exportDisabled: exporting || rest.isLoading,
        loading: rest.isLoading,
    }

    return { chartProps, data, ...rest }
}
